import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { api } from "../../common/setupApi";
import { selectSelectedEvent } from "../events/eventsSlice";
import {
  getWellnessMarkers,
  selectSessionsMarkers,
} from "../markers/markersSlice";
import { selectSelectedPlayer, selectSquads } from "../squad/squadSlice";
import { selectLang } from "../lang/langSlice";
import { formatMarkers } from "../markers/format";
import { handleApiError } from "../alerts/alertsSlice";

export const getEventQuestionnaire = createAsyncThunk(
  "questionnaire/getEvent",
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const eventId = selectSelectedEvent(state).id;
    const lang = selectLang(state);
    return api
      .get(`session-questionnaire/?session=${eventId}&lang=${lang}`)
      .then((res) => {
        return { data: res.data, eventId };
      })
      .catch((error) => {
        dispatch(handleApiError(error));
        return rejectWithValue(error?.response?.data);
      });
  },
  {
    condition: (_, { getState }) => {
      const { status } = selectEventQuestionnaire(getState());
      if (status === "pending") {
        return false;
      }
    },
  }
);

export const getEventCompletion = createAsyncThunk(
  "questionnaire/getCompletion",
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const eventId = selectSelectedEvent(state).id;
    return api
      .get(`session-questionnaire-answers/?session=${eventId}`)
      .then((res) => {
        return {
          data: res.data
            .filter((d) => d.questionnaire_completed)
            .map((d) => d.player),
          eventId,
        };
      })
      .catch((error) => {
        dispatch(handleApiError(error));
        return rejectWithValue(error?.response?.data);
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      const { status } = selectEventCompletion(state);
      if (status === "pending") {
        return false;
      }
    },
  }
);

// Le joueur get sa completion des events questionnaires
export const getPlayerCompletion = createAsyncThunk(
  "questionnaire/getPlayerCompletion",
  async (params, { dispatch, getState, rejectWithValue }) => {
    const playerPk = selectSelectedPlayer(getState()).pk;
    return api
      .get(`session-questionnaire-answers/?player=${playerPk || params?.pk}`)
      .then((res) => {
        return { data: res.data, pk: playerPk || params?.pk };
      })
      .catch((error) => {
        dispatch(handleApiError(error));
        return rejectWithValue(error?.response?.data);
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      const { status } = selectPlayerCompletion(state);
      if (status === "pending") {
        return false;
      }
    },
  }
);

const initialState = {
  wellness: {
    open: false,
    questions: {
      status: "idle",
      error: null,
      data: {},
    },
  },
  // event sélectionné
  event: {
    open: false,
    questions: {
      eventId: null,
      status: "idle",
      error: null,
      data: {},
    },
    completion: {
      eventId: null,
      status: "idle",
      error: null,
      data: [],
    },
  },
  pains: {
    open: false,
  },
  // questionnaire sélectionné
  selected: {
    id: null, // questionnaireId
    questions: {},
  },
  //
  drawerPlayers: "closed",
  // completion du joueur sélectionné
  playerCompletion: {
    pk: null,
    status: "idle",
    error: null,
    data: [],
  },
};

const questionnaireSlice = createSlice({
  name: "questionnaire",
  initialState,
  reducers: {
    openDrawerPlayers: (state, action) => {
      state.drawerPlayers = action.payload;
    },
    closeDrawerPlayers: (state) => {
      state.drawerPlayers = "closed";
    },
    openQuestionnaire: (state, action) => {
      const questionnaireType = action.payload.questionnaireType;
      state[questionnaireType].open = true;
      if (questionnaireType === "wellness" || questionnaireType === "event") {
        state.selected.questions = state[questionnaireType].questions.data;
        state.selected.id = action.payload.questionnaireId || null;
      }
    },
    closeQuestionnaire: (state) => {
      state.pains.open = false;
      state.wellness.open = false;
      state.event.open = false;
      state.selected = { id: null, questions: {} };
    },
    setEventCompletion: (state, action) => {
      state.event.completion.data.push(action.payload.playerPk);
    },
    setPlayerCompletion: (state, action) => {
      state.playerCompletion.data = state.playerCompletion.data.map((d) =>
        d.id === action.payload.questionnaireId
          ? {
              ...d,
              questionnaire_completed: true,
            }
          : d
      );
    },
    sortWellnessQuestionnaire: (state, action) => {
      state.wellness.questions.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEventQuestionnaire.pending, (state) => {
        state.event.questions.status = "pending";
        state.event.questions.error = null;
      })
      .addCase(getEventQuestionnaire.fulfilled, (state, action) => {
        state.event.questions.status = "fulfilled";
        state.event.questions.data = action.payload.data;
        state.event.questions.eventId = action.payload.eventId;
      })
      .addCase(getEventQuestionnaire.rejected, (state, action) => {
        state.event.questions.status = "rejected";
        state.event.questions.error = action.payload;
      })
      .addCase(getEventCompletion.pending, (state) => {
        state.event.completion.status = "pending";
        state.event.completion.error = null;
      })
      .addCase(getEventCompletion.fulfilled, (state, action) => {
        state.event.completion.status = "fulfilled";
        state.event.completion.data = action.payload.data;
        state.event.completion.eventId = action.payload.eventId;
      })
      .addCase(getEventCompletion.rejected, (state, action) => {
        state.event.completion.status = "rejected";
        state.event.completion.error = action.payload;
      })
      .addCase(getWellnessMarkers.pending, (state) => {
        state.wellness.questions.status = "pending";
        state.wellness.questions.error = null;
      })
      .addCase(getWellnessMarkers.fulfilled, (state, action) => {
        state.wellness.questions.status = "fulfilled";
        state.wellness.questions.data = action.payload;
      })
      .addCase(getWellnessMarkers.rejected, (state, action) => {
        state.wellness.questions.status = "rejected";
        state.wellness.questions.error = action.payload;
      })
      .addCase(getPlayerCompletion.pending, (state) => {
        state.playerCompletion.status = "pending";
        state.playerCompletion.error = null;
      })
      .addCase(getPlayerCompletion.fulfilled, (state, action) => {
        state.playerCompletion.status = "fulfilled";
        state.playerCompletion.data = action.payload.data;
        state.playerCompletion.pk = action.payload.pk;
      })
      .addCase(getPlayerCompletion.rejected, (state, action) => {
        state.playerCompletion.status = "rejected";
        state.playerCompletion.error = action.payload;
      })
      .addCase("markers/add", (state, action) => {
        const { questionnaireType, markerType, data } = action.payload;
        if (
          questionnaireType === "wellness" &&
          !state.wellness.questions.data[markerType].find(
            (d) => d.id === data.id
          )
        ) {
          state.wellness.questions.data[markerType].push(data);
        }
      })
      .addCase("markers/set", (state, action) => {
        const { questionnaireType, markerType, data } = action.payload;
        if (questionnaireType === "wellness") {
          state.wellness.questions.data[markerType] =
            state.wellness.questions.data[markerType].map((d) =>
              d.id === data.id ? data : d
            );
        }
      })
      .addCase("markers/delete", (state, action) => {
        const { questionnaireType, markerType, id } = action.payload;
        if (questionnaireType === "wellness") {
          state.wellness.questions.data[markerType] =
            state.wellness.questions.data[markerType].filter(
              (d) => d.id !== id
            );
        }
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const questionnaireActions = questionnaireSlice.actions;

const _selectWellnessQuestionnaire = (state) => state.questionnaire.wellness;

export const selectWellnessQuestionnaire = createSelector(
  [(state) => _selectWellnessQuestionnaire(state)],
  (wellness) => {
    const data = wellness.questions.data;
    const status = wellness.questions.status;
    const error = wellness.questions.error;
    const open = wellness.open;
    const isLoading = status === "pending";

    const { perc, scale, quanti, duration, bool, mcq, qst, sorted } =
      formatMarkers(data);

    return {
      data,
      status,
      error,
      isLoading,
      open,
      items: sorted,
      perc,
      scale,
      quanti,
      duration,
      bool,
      mcq,
      qst,
    };
  }
);

const _selectEventQuestionnaire = (state) => state.questionnaire.event;

export const selectEventQuestionnaire = createSelector(
  [(state) => _selectEventQuestionnaire(state)],
  (event) => {
    const eventId = event.questions.eventId;
    const data = event.questions.data;
    const status = event.questions.status;
    const error = event.questions.error;
    const open = event.open;
    const isLoading = status === "pending";

    const { perc, scale, quanti, duration, bool, mcq, qst, sorted } =
      formatMarkers(data);

    return {
      data,
      status,
      error,
      isLoading,
      eventId,
      open,
      items: sorted,
      perc,
      scale,
      quanti,
      duration,
      bool,
      mcq,
      qst,
    };
  }
);

const _selectEventCompletion = (state) => state.questionnaire.event.completion;

export const selectEventCompletion = createSelector(
  [(state) => _selectEventCompletion(state)],
  (completion) => {
    const eventId = completion.eventId;
    const data = completion.data;
    const status = completion.status;
    const error = completion.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading, eventId };
  }
);

export const selectOpenDrawerPlayers = (state) =>
  state.questionnaire.drawerPlayers;

const _selectSelectedQuestionnaire = (state) => state.questionnaire.selected;

export const selectSelectedQuestionnaire = createSelector(
  [(state) => _selectSelectedQuestionnaire(state)],
  (selected) => {
    const data = selected.questions;
    const questionnaireId = selected.id;

    const { perc, scale, quanti, duration, bool, mcq, qst, sorted } =
      formatMarkers(data);

    return {
      questionnaireId,
      items: sorted,
      perc,
      scale,
      quanti,
      duration,
      bool,
      mcq,
      qst,
    };
  }
);

export const selectIsWellnessCompleted = (state) =>
  state.squad.players.selected.wellness_answered;

const _selectPlayerCompletion = (state) => state.questionnaire.playerCompletion;

export const selectPlayerCompletion = createSelector(
  [(state) => _selectPlayerCompletion(state)],
  (playerCompletion) => {
    const data = playerCompletion.data;
    const status = playerCompletion.status;
    const error = playerCompletion.error;
    const playerPk = playerCompletion.pk;
    const isLoading = status === "pending";
    return { data, status, error, isLoading, playerPk };
  }
);

// memo ok
export const selectPlayerUncompletion = createSelector(
  [(state) => selectPlayerCompletion(state).data],
  (data) => {
    const uncompleted = data.filter((d) => d.questionnaire_completed !== true);
    return { uncompleted, uncompletedCounts: uncompleted.length };
  }
);

export const selectOpenPainsQuestionnaire = (state) =>
  state.questionnaire.pains.open;

export const selectIsWellnessActive = createSelector(
  [(state) => selectSquads(state)],
  (squads) => {
    if (squads?.status !== "fulfilled") return false;

    const activeSquad = squads?.data?.find((d) => d.activated);
    const value = activeSquad?.wellness_active;

    if (value == null) return true;
    return value;
  }
);

const searchSessionTypeMarkers = (data, sessionId) =>
  data.filter(
    (d) => d.session_types.find((s) => s.id === sessionId) !== undefined
  );

export const selectSessionTypeQuestionnaire = createSelector(
  [
    (state) => selectSessionsMarkers(state),
    (_, sessionTypeId) => sessionTypeId,
  ],
  (markers, sessionTypeId) => {
    const { items, perc, scale, quanti, duration, bool, mcq, qst } = markers;
    return {
      items: searchSessionTypeMarkers(items, sessionTypeId),
      perc: searchSessionTypeMarkers(perc, sessionTypeId),
      scale: searchSessionTypeMarkers(scale, sessionTypeId),
      quanti: searchSessionTypeMarkers(quanti, sessionTypeId),
      duration: searchSessionTypeMarkers(duration, sessionTypeId),
      bool: searchSessionTypeMarkers(bool, sessionTypeId),
      mcq: searchSessionTypeMarkers(mcq, sessionTypeId),
      qst: searchSessionTypeMarkers(qst, sessionTypeId),
    };
  }
);

export default questionnaireSlice.reducer;
